import { useState, useCallback, useEffect } from "react";
import { useAtom, useAtomValue } from "jotai";
import { SelectedFundAtom } from "@/store/FundStateAtom";
import { SelectedReportingPeriodAtom } from "@/store/ReportingPeriodStateAtom";
import { getTransactions } from "@/api/Transactions";
import { TransactionWithInstrumentAndAccount } from "@/types/Transaction";
import { groupTransactionsByCategory } from "./transactionCategories";
import { ReportingPeriod } from "@/types/ReportingPeriod";

export function useExplainerController() {
  const selectedFund = useAtomValue(SelectedFundAtom);
  const [selectedPeriod, setSelectedPeriod] = useAtom(SelectedReportingPeriodAtom);
  const [comparisonPeriod, setComparisonPeriod] = useState<ReportingPeriod | null>(null);
  const [periodTransactions, setPeriodTransactions] = useState<Record<string, TransactionWithInstrumentAndAccount[]>>({});
  const [comparisonTransactions, setComparisonTransactions] = useState<Record<string, TransactionWithInstrumentAndAccount[]>>({});
  const [isLoading, setIsLoading] = useState(false);

  // Helper function to get transaction count for a period
  const getPeriodTransactionCount = useCallback((periodId: string): number => {
    return periodTransactions[periodId]?.length || 0;
  }, [periodTransactions]);

  // Get previous period ID (assuming sequential numbering) - used as fallback
  const getPreviousPeriodId = useCallback((currentPeriodId: string): string => {
    const currentId = parseInt(currentPeriodId);
    return (currentId - 1).toString();
  }, []);

  // Fetch transactions for selected period and comparison period
  const fetchTransactions = useCallback(async () => {
    if (!selectedFund || !selectedPeriod) return;

    setIsLoading(true);
    try {
      // Fetch current period transactions
      const transactions = await getTransactions(selectedFund.fundId, parseInt(selectedPeriod.reportingPeriodId));
      setPeriodTransactions(prev => ({
        ...prev,
        [selectedPeriod.reportingPeriodId]: transactions || []
      }));

      // Fetch comparison period transactions
      const comparisonPeriodId = comparisonPeriod?.reportingPeriodId || getPreviousPeriodId(selectedPeriod.reportingPeriodId);
      try {
        const comparisonTransactionData = await getTransactions(selectedFund.fundId, parseInt(comparisonPeriodId));
        setComparisonTransactions(prev => ({
          ...prev,
          [comparisonPeriodId]: comparisonTransactionData || []
        }));
      } catch (error) {
        console.log("No comparison period data available:", error);
        setComparisonTransactions(prev => ({
          ...prev,
          [comparisonPeriodId]: []
        }));
      }
    } catch (error) {
      console.error("Error fetching transactions:", error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedFund, selectedPeriod, comparisonPeriod, getPreviousPeriodId]);

  // Get grouped transaction data for table display
  const getGroupedTransactionData = useCallback(() => {
    if (!selectedPeriod || !periodTransactions[selectedPeriod.reportingPeriodId]) {
      return null;
    }

    const currentTransactions = periodTransactions[selectedPeriod.reportingPeriodId];
    const comparisonPeriodId = comparisonPeriod?.reportingPeriodId || getPreviousPeriodId(selectedPeriod.reportingPeriodId);
    const comparisonTransactionData = comparisonTransactions[comparisonPeriodId] || [];
    
    const currentCategories = groupTransactionsByCategory(currentTransactions);
    const comparisonCategories = groupTransactionsByCategory(comparisonTransactionData);

    // Get all category keys from both periods
    const allCategoryKeys = new Set([
      ...Object.keys(currentCategories),
      ...Object.keys(comparisonCategories)
    ]);

    // Create unified transaction lists for each category that include both current and comparison transactions
    const unifiedCategories = Array.from(allCategoryKeys).reduce((acc, categoryKey) => {
      const category = categoryKey as keyof typeof currentCategories;
      
      // Get all unique transactions by creating a map keyed by transaction type + instrument
      const transactionMap = new Map<string, {
        current: TransactionWithInstrumentAndAccount | null;
        comparison: TransactionWithInstrumentAndAccount | null;
        key: string;
        transactionType: string;
        instrumentName: string;
      }>();

      // Add current period transactions (if category exists)
      if (currentCategories[category]) {
        currentCategories[category].transactions.forEach(transaction => {
          const key = `${transaction.transactionType}-${transaction.instrument?.name || 'N/A'}`;
          transactionMap.set(key, {
            current: transaction,
            comparison: null,
            key,
            transactionType: transaction.transactionType,
            instrumentName: transaction.instrument?.name || 'N/A'
          });
        });
      }

      // Add comparison period transactions and match with current
      if (comparisonCategories[category]) {
        comparisonCategories[category].transactions.forEach(transaction => {
          const key = `${transaction.transactionType}-${transaction.instrument?.name || 'N/A'}`;
          if (transactionMap.has(key)) {
            // Update existing entry with comparison data
            const existing = transactionMap.get(key)!;
            transactionMap.set(key, { ...existing, comparison: transaction });
          } else {
            // Add new entry for comparison-only transaction
            transactionMap.set(key, {
              current: null,
              comparison: transaction,
              key,
              transactionType: transaction.transactionType,
              instrumentName: transaction.instrument?.name || 'N/A'
            });
          }
        });
      }

      acc[category] = {
        name: currentCategories[category]?.name || comparisonCategories[category]?.name || categoryKey,
        currentTotal: currentCategories[category]?.total || 0,
        comparisonTotal: comparisonCategories[category]?.total || 0,
        unifiedTransactions: Array.from(transactionMap.values()).sort((a, b) => 
          a.transactionType.localeCompare(b.transactionType)
        )
      };

      return acc;
    }, {} as Record<string, {
      name: string;
      currentTotal: number;
      comparisonTotal: number;
      unifiedTransactions: Array<{
        current: TransactionWithInstrumentAndAccount | null;
        comparison: TransactionWithInstrumentAndAccount | null;
        key: string;
        transactionType: string;
        instrumentName: string;
      }>;
    }>);

    return {
      current: currentCategories,
      comparison: comparisonCategories,
      unified: unifiedCategories,
      currentTotal: currentTransactions.reduce((sum, t) => sum + t.baseAmount, 0),
      comparisonTotal: comparisonTransactionData.reduce((sum, t) => sum + t.baseAmount, 0)
    };
  }, [selectedPeriod, periodTransactions, comparisonTransactions, comparisonPeriod, getPreviousPeriodId]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  return {
    // State
    selectedFund,
    selectedPeriod,
    comparisonPeriod,
    periodTransactions,
    comparisonTransactions,
    isLoading,
    
    // Helper functions
    getPeriodTransactionCount,
    getGroupedTransactionData,
    
    // Actions
    setSelectedPeriod,
    setComparisonPeriod,
  };
} 